import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { getFullName, getInitials } from "@/lib/userUtils";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import AvatarSourceEditor from "@/components/avatars/AvatarSourceEditor";
import { markdownToPlain, plainExcerpt } from "@/lib/markdownPlain";
import { permalinkForPost } from "@/lib/permalinks";
import { listSavedPosts } from "@/lib/savedPostsApi";
import PostInteractions from "@/components/PostInteractions";
import {
  clearMemberAvatar,
  setMemberAvatarUrl,
  uploadMemberAvatar,
  updateMemberTitle,
} from "@/lib/avatarApi";
import { toastError, toastSuccess } from "@/lib/toast";
import { notifyOrgBrandChanged } from "../scripts/OrgBrandThemeProvider";
import PostComposer from "./PostComposer";
import PostAttachmentChips from "@/components/PostAttachmentChips";
import { uploadPostAttachments } from "@/lib/postAttachments";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import {
  formatOrgPhoneInput,
  hasEmptyExtMarker,
  isValidOrgEmail,
  isValidOrgPhone,
  stripEmptyExtMarker,
} from "@/lib/contactValidation";

import { API_BASE_URL } from "@/lib/apiBase";
import CircaAccountPanel from "@/components/CircaAccountPanel";
import CircatronMyBotsSection from "@/components/CircatronMyBotsSection";
import NotificationPrefsPanel from "@/components/NotificationPrefsPanel";

function formatRelativeTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d ago`;
  return d.toLocaleDateString();
}

function pendingCriticalFromMetadata(metadata) {
  if (!metadata) return false;
  try {
    const parsed =
      typeof metadata === "string" ? JSON.parse(metadata) : metadata;
    return Boolean(parsed?.pending_critical);
  } catch {
    return false;
  }
}

const TITLE_MAX_LEN = 100;

const Profile = () => {
  const { accessToken, login, logout } = useAuth();
  const { selectedOrgId } = useOrgFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get("tab");
    return tab === "settings" ||
      tab === "account" ||
      tab === "bots" ||
      tab === "posts" ||
      tab === "saved" ||
      tab === "teams" ||
      tab === "drafts"
      ? tab
      : "profile";
  });
  const [memberships, setMemberships] = useState([]);
  const [memberAvatarUrl, setMemberAvatarUrlState] = useState(null);
  const [memberTitle, setMemberTitle] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [titleSaving, setTitleSaving] = useState(false);
  const [titleSuccess, setTitleSuccess] = useState(false);
  const [titleError, setTitleError] = useState(null);
  const [orgEmail, setOrgEmail] = useState("");
  const [orgEmailDraft, setOrgEmailDraft] = useState("");
  const [orgEmailSaving, setOrgEmailSaving] = useState(false);
  const [orgEmailSuccess, setOrgEmailSuccess] = useState(false);
  const [orgEmailError, setOrgEmailError] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneDraft, setPhoneDraft] = useState("");
  const [officeLocation, setOfficeLocation] = useState("");
  const [officeDraft, setOfficeDraft] = useState("");
  const [contactSaving, setContactSaving] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState(null);
  const [useOrgBranding, setUseOrgBranding] = useState(true);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [postHistory, setPostHistory] = useState([]);
  const [postHistoryTotal, setPostHistoryTotal] = useState(0);
  const [postHistoryLoading, setPostHistoryLoading] = useState(false);
  const [postHistoryError, setPostHistoryError] = useState(null);
  const [savedPosts, setSavedPosts] = useState([]);
  const [savedPostsTotal, setSavedPostsTotal] = useState(0);
  const [savedPostsLoading, setSavedPostsLoading] = useState(false);
  const [savedPostsError, setSavedPostsError] = useState(null);
  const [memberTeams, setMemberTeams] = useState([]);
  const [memberTeamsLoading, setMemberTeamsLoading] = useState(false);
  const [memberTeamsError, setMemberTeamsError] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [draftsTotal, setDraftsTotal] = useState(0);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftsError, setDraftsError] = useState(null);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [draftComposerKey, setDraftComposerKey] = useState(0);
  const [draftComposerDestinations, setDraftComposerDestinations] = useState([]);
  const [canOrgPostCritical, setCanOrgPostCritical] = useState(false);
  const [draftAdminTeamIds, setDraftAdminTeamIds] = useState(() => new Set());
  const [draftActionLoading, setDraftActionLoading] = useState(false);

  const activeOrgId =
    selectedOrgId && selectedOrgId !== "social" ? String(selectedOrgId) : null;

  const activeOrg = useMemo(
    () => memberships.find((org) => org.org_id === activeOrgId) || null,
    [memberships, activeOrgId]
  );

  axios.defaults.withCredentials = true;

  // Helper function to make authenticated API calls with token refresh
  const makeAuthenticatedRequest = async (requestFn, token = accessToken) => {
    try {
      return await requestFn(token);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        if (err.response.data.message === "Token has expired") {
          try {
            const refreshResponse = await axios.post(
              `${API_BASE_URL}/auth/refresh`,
              null,
              {
                withCredentials: true,
              }
            );
            const newAccessToken = refreshResponse.data.access_token || refreshResponse.data.accessToken;
            login(newAccessToken);
            return await requestFn(newAccessToken);
          } catch (refreshError) {
            console.error("Failed to refresh access token:", refreshError);
            logout(); // Logout user when refresh fails
            throw new Error("Session expired. Please log in again.");
          }
        }
      }
      throw err;
    }
  };

  const fetchUserData = async (token) => {
    const response = await axios.get(`${API_BASE_URL}/user/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const fetchMemberships = async (token) => {
    const response = await axios.get(`${API_BASE_URL}/org/memberships`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data || [];
  };

  const fetchMyMembershipProfile = async (token, orgId, userId) => {
    const response = await axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const mine = (response.data || []).find(
      (m) => m.user?.user_id === userId || m.user_id === userId
    );
    return {
      member_avatar_url:
        mine?.member_avatar_url ||
        mine?.user?.member_avatar_url ||
        null,
      title: mine?.title || mine?.user?.title || null,
      org_email: mine?.org_email ?? null,
    };
  };

  const fetchMemberTeams = async (token, orgId) => {
    const membershipsRes = await axios.get(`${API_BASE_URL}/team/memberships`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { org_id: orgId },
    });
    const memberTeamsList = Array.isArray(membershipsRes.data)
      ? membershipsRes.data
      : [];
    return memberTeamsList
      .map((team) => {
        const id = team.team_id ?? team.teamId;
        if (!id) return null;
        const role = String(team.role || "member").toLowerCase();
        return {
          team_id: Number(id),
          name: team.name || `Team ${id}`,
          description: team.description || "",
          status: role === "admin" ? "admin" : "member",
        };
      })
      .filter(Boolean)
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      );
  };

  const fetchMyPosts = async (token, orgId, offset = 0) => {
    const response = await axios.get(`${API_BASE_URL}/posts/mine`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { org_id: orgId, limit: 20, offset },
    });
    return {
      posts: Array.isArray(response.data?.posts) ? response.data.posts : [],
      total: Number(response.data?.total) || 0,
    };
  };

  const fetchMyDrafts = async (token, orgId) => {
    const response = await axios.get(`${API_BASE_URL}/posts/drafts`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { org_id: orgId, limit: 50, offset: 0 },
    });
    return {
      posts: Array.isArray(response.data?.posts) ? response.data.posts : [],
      total: Number(response.data?.total) || 0,
    };
  };

  const fetchSavedPosts = async (token, orgId, offset = 0) => {
    return listSavedPosts(token, orgId, { limit: 20, offset });
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (
      value === "settings" ||
      value === "account" ||
      value === "bots" ||
      value === "posts" ||
      value === "saved" ||
      value === "teams" ||
      value === "drafts"
    ) {
      next.set("tab", value);
    } else {
      next.delete("tab");
    }
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!accessToken) return;

    const loadUserData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [userData, orgs] = await Promise.all([
          makeAuthenticatedRequest(fetchUserData),
          makeAuthenticatedRequest(fetchMemberships).catch(() => []),
        ]);
        setUser(userData);
        setMemberships(Array.isArray(orgs) ? orgs : []);

        setLoading(false);
      } catch (err) {
        console.error("Failed to load user data:", err);
        setError(err.message || "Failed to load profile data");
        setLoading(false);
      }
    };

    loadUserData();
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (
      (tab === "settings" ||
        tab === "account" ||
        tab === "bots" ||
        tab === "posts" ||
        tab === "saved" ||
        tab === "teams" ||
        tab === "drafts") &&
      activeTab !== tab
    ) {
      setActiveTab(tab);
    } else if (!tab && activeTab !== "profile") {
      setActiveTab("profile");
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== "posts") return;
    if (!accessToken || !activeOrgId) {
      setPostHistory([]);
      setPostHistoryTotal(0);
      setPostHistoryError(null);
      setPostHistoryLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setPostHistoryLoading(true);
      setPostHistoryError(null);
      try {
        const result = await makeAuthenticatedRequest((token) =>
          fetchMyPosts(token, activeOrgId, 0)
        );
        if (!cancelled) {
          setPostHistory(result.posts);
          setPostHistoryTotal(result.total);
        }
      } catch (err) {
        console.error("Failed to load post history:", err);
        if (!cancelled) {
          setPostHistory([]);
          setPostHistoryTotal(0);
          setPostHistoryError(
            err.response?.data?.message || "Could not load your post history."
          );
        }
      } finally {
        if (!cancelled) setPostHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, accessToken, activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== "saved") return;
    if (!accessToken || !activeOrgId) {
      setSavedPosts([]);
      setSavedPostsTotal(0);
      setSavedPostsError(null);
      setSavedPostsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setSavedPostsLoading(true);
      setSavedPostsError(null);
      try {
        const result = await makeAuthenticatedRequest((token) =>
          fetchSavedPosts(token, activeOrgId, 0)
        );
        if (!cancelled) {
          setSavedPosts(result.posts || []);
          setSavedPostsTotal(Number(result.total) || 0);
        }
      } catch (err) {
        console.error("Failed to load saved posts:", err);
        if (!cancelled) {
          setSavedPosts([]);
          setSavedPostsTotal(0);
          setSavedPostsError(
            err.response?.data?.message || "Could not load saved posts."
          );
        }
      } finally {
        if (!cancelled) setSavedPostsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, accessToken, activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== "drafts") return;
    if (!accessToken || !activeOrgId) {
      setDrafts([]);
      setDraftsTotal(0);
      setDraftsError(null);
      setDraftsLoading(false);
      setEditingDraftId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setDraftsLoading(true);
      setDraftsError(null);
      try {
        const [result, meRes, teams] = await Promise.all([
          makeAuthenticatedRequest((token) => fetchMyDrafts(token, activeOrgId)),
          axios
            .get(`${API_BASE_URL}/org/${activeOrgId}/me`, {
              headers: { Authorization: `Bearer ${accessToken}` },
            })
            .catch(() => ({ data: {} })),
          makeAuthenticatedRequest((token) =>
            fetchMemberTeams(token, activeOrgId)
          ).catch(() => []),
        ]);
        if (cancelled) return;
        setDrafts(result.posts);
        setDraftsTotal(result.total);

        const caps = new Set(meRes?.data?.capabilities || []);
        const canOrgPost =
          Boolean(meRes?.data?.is_owner) || caps.has("org.post");
        setCanOrgPostCritical(canOrgPost);

        const adminIds = new Set(
          (teams || [])
            .filter((t) => t.status === "admin")
            .map((t) => Number(t.team_id))
        );
        setDraftAdminTeamIds(adminIds);

        const destinations = [];
        if (canOrgPost) {
          destinations.push({
            type: "org",
            id: activeOrgId,
            label: activeOrg?.name
              ? `${activeOrg.name} (organization)`
              : "Organization",
          });
        }
        for (const t of teams || []) {
          destinations.push({
            type: "team",
            id: t.team_id,
            label: t.name,
          });
        }
        setDraftComposerDestinations(destinations);
      } catch (err) {
        console.error("Failed to load drafts:", err);
        if (!cancelled) {
          setDrafts([]);
          setDraftsTotal(0);
          setDraftsError(
            err.response?.data?.message || "Could not load drafts."
          );
        }
      } finally {
        if (!cancelled) setDraftsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, accessToken, activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const editingDraft = useMemo(
    () =>
      drafts.find(
        (d) => String(d.post_id ?? d.postId) === String(editingDraftId)
      ) || null,
    [drafts, editingDraftId]
  );

  const refreshDrafts = async () => {
    if (!accessToken || !activeOrgId) return;
    const result = await makeAuthenticatedRequest((token) =>
      fetchMyDrafts(token, activeOrgId)
    );
    setDrafts(result.posts);
    setDraftsTotal(result.total);
  };

  const handleDraftAutosave = async (payload) => {
    if (!editingDraftId || !activeOrgId || !accessToken) return;
    const body = {
      title: payload.title,
      content: payload.content,
      tags: payload.tags,
      critical: Boolean(payload.critical),
      references: payload.references,
    };
    if (payload.team_id != null) {
      body.team_id = payload.team_id;
    } else {
      body.org_level = true;
    }
    await axios.patch(
      `${API_BASE_URL}/posts/${editingDraftId}?org_id=${encodeURIComponent(activeOrgId)}`,
      body,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (payload.files?.length) {
      try {
        await uploadPostAttachments({
          postId: editingDraftId,
          orgId: activeOrgId,
          files: payload.files,
          accessToken,
        });
      } catch (attachErr) {
        console.error("Failed to upload draft attachments:", attachErr);
        toastError(attachErr, "Draft saved, but file upload failed");
        throw attachErr;
      }
    }
    await refreshDrafts();
  };

  const handleDraftPublish = async (payload) => {
    if (!editingDraftId || !activeOrgId || !accessToken) return;
    setDraftActionLoading(true);
    try {
      // Persist latest edits then publish
      await handleDraftAutosave(payload);
      const { data: published } = await axios.post(
        `${API_BASE_URL}/posts/${editingDraftId}/publish?org_id=${encodeURIComponent(activeOrgId)}`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess("Draft published");
      setEditingDraftId(null);
      await refreshDrafts();
      const postId = published?.post_id ?? published?.postId;
      if (postId != null) {
        navigate(permalinkForPost(activeOrgId, postId));
      }
    } catch (err) {
      toastError(err, "Failed to publish draft");
      throw err;
    } finally {
      setDraftActionLoading(false);
    }
  };

  const handleDraftDiscard = async () => {
    if (!editingDraftId || !activeOrgId || !accessToken) return;
    const ok = await confirm({
      title: "Discard draft?",
      description: "Discard this draft? This cannot be undone.",
      confirmLabel: "Discard",
      variant: "destructive",
    });
    if (!ok) return;
    setDraftActionLoading(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/posts/${editingDraftId}?org_id=${encodeURIComponent(activeOrgId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess("Draft discarded");
      setEditingDraftId(null);
      await refreshDrafts();
    } catch (err) {
      toastError(err, "Failed to discard draft");
    } finally {
      setDraftActionLoading(false);
    }
  };

  const handleLoadMorePosts = async () => {
    if (
      !accessToken ||
      !activeOrgId ||
      postHistoryLoading ||
      postHistory.length >= postHistoryTotal
    ) {
      return;
    }
    setPostHistoryLoading(true);
    setPostHistoryError(null);
    try {
      const result = await makeAuthenticatedRequest((token) =>
        fetchMyPosts(token, activeOrgId, postHistory.length)
      );
      setPostHistory((prev) => [...prev, ...result.posts]);
      setPostHistoryTotal(result.total);
    } catch (err) {
      console.error("Failed to load more post history:", err);
      setPostHistoryError(
        err.response?.data?.message || "Could not load more posts."
      );
    } finally {
      setPostHistoryLoading(false);
    }
  };

  const handleLoadMoreSaved = async () => {
    if (
      !accessToken ||
      !activeOrgId ||
      savedPostsLoading ||
      savedPosts.length >= savedPostsTotal
    ) {
      return;
    }
    setSavedPostsLoading(true);
    setSavedPostsError(null);
    try {
      const result = await makeAuthenticatedRequest((token) =>
        fetchSavedPosts(token, activeOrgId, savedPosts.length)
      );
      setSavedPosts((prev) => [...prev, ...(result.posts || [])]);
      setSavedPostsTotal(Number(result.total) || 0);
    } catch (err) {
      console.error("Failed to load more saved posts:", err);
      setSavedPostsError(
        err.response?.data?.message || "Could not load more saved posts."
      );
    } finally {
      setSavedPostsLoading(false);
    }
  };

  const handleSavedChange = (postId, saved) => {
    if (saved) return;
    const id = String(postId);
    setSavedPosts((prev) =>
      prev.filter((p) => String(p.post_id ?? p.postId) !== id)
    );
    setSavedPostsTotal((n) => Math.max(0, n - 1));
  };

  const patchSavedPostEngagement = (postId, patch) => {
    const id = String(postId);
    setSavedPosts((prev) =>
      prev.map((p) => {
        if (String(p.post_id ?? p.postId) !== id) return p;
        return {
          ...p,
          ...patch,
          like_count: patch.likeCount ?? patch.like_count ?? p.like_count,
          comment_count:
            patch.commentCount ?? patch.comment_count ?? p.comment_count,
          is_liked: patch.isLiked ?? patch.is_liked ?? p.is_liked,
        };
      })
    );
  };

  const handleSavedLike = async (post) => {
    if (!accessToken || !user?.user_id || !activeOrgId) return;
    const postId = post.post_id ?? post.postId;
    if (postId == null) return;

    const currentlyLiked = !!(post.is_liked ?? post.isLiked);
    const prevLikeCount = Number(post.like_count ?? post.likeCount ?? 0);
    const nextLiked = !currentlyLiked;
    const nextLikeCount = Math.max(0, prevLikeCount + (nextLiked ? 1 : -1));
    patchSavedPostEngagement(postId, {
      likeCount: nextLikeCount,
      isLiked: nextLiked,
    });

    try {
      await makeAuthenticatedRequest(async (token) => {
        if (currentlyLiked) {
          const { data } = await axios.get(
            `${API_BASE_URL}/posts/${postId}/interactions`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: { org_id: activeOrgId },
            }
          );
          const interactions = Array.isArray(data) ? data : [];
          const userLike = interactions.find(
            (i) =>
              (i.interactionType === "like" || i.interaction_type === "like") &&
              !(i.parentId ?? i.parent_id) &&
              (i.userId === user.user_id || i.user_id === user.user_id)
          );
          if (userLike) {
            await axios.delete(
              `${API_BASE_URL}/posts/interactions/${
                userLike.interactionId || userLike.interaction_id
              }`,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: { org_id: activeOrgId },
              }
            );
          }
        } else {
          await axios.post(
            `${API_BASE_URL}/posts/${postId}/interactions`,
            { interaction_type: "like" },
            {
              headers: { Authorization: `Bearer ${token}` },
              params: { org_id: activeOrgId },
            }
          );
        }
      });
    } catch (err) {
      console.error("Failed to toggle like on saved post:", err);
      patchSavedPostEngagement(postId, {
        likeCount: prevLikeCount,
        isLiked: currentlyLiked,
      });
    }
  };

  useEffect(() => {
    if (!accessToken || !user?.user_id || !activeOrgId) {
      setMemberAvatarUrlState(null);
      setMemberTitle("");
      setTitleDraft("");
      setTitleSuccess(false);
      setTitleError(null);
      setOrgEmail("");
      setOrgEmailDraft("");
      setOrgEmailSuccess(false);
      setOrgEmailError(null);
      setPhoneNumber("");
      setPhoneDraft("");
      setOfficeLocation("");
      setOfficeDraft("");
      setContactSuccess(false);
      setContactError(null);
      setUseOrgBranding(true);
      setMembershipError(null);
      setMembershipLoading(false);
      return;
    }

    let cancelled = false;
    const loadMembershipProfile = async () => {
      setMembershipLoading(true);
      setMembershipError(null);
      setTitleError(null);
      setTitleSuccess(false);
      setOrgEmailError(null);
      setOrgEmailSuccess(false);
      setContactError(null);
      setContactSuccess(false);
      try {
        const [profile, meCaps] = await Promise.all([
          makeAuthenticatedRequest((token) =>
            fetchMyMembershipProfile(token, activeOrgId, user.user_id)
          ),
          makeAuthenticatedRequest(async (token) => {
            const res = await axios.get(`${API_BASE_URL}/org/${activeOrgId}/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            return res.data;
          }),
        ]);
        if (!cancelled) {
          setMemberAvatarUrlState(profile.member_avatar_url);
          const title = profile.title || "";
          setMemberTitle(title);
          setTitleDraft(title);
          // Use the raw membership org_email (null until the member sets it).
          // Do not fall back to the platform email here so the editor reflects
          // the true unset state; contact display falls back separately.
          const email = meCaps?.org_email || "";
          setOrgEmail(email);
          setOrgEmailDraft(email);
          const phone = meCaps?.phone_number || "";
          setPhoneNumber(phone);
          setPhoneDraft(phone);
          const office = meCaps?.office_location || "";
          setOfficeLocation(office);
          setOfficeDraft(office);
          setUseOrgBranding(meCaps?.use_org_branding !== false);
        }
      } catch (err) {
        console.error("Failed to load membership profile:", err);
        if (!cancelled) {
          setMemberAvatarUrlState(null);
          setMemberTitle("");
          setTitleDraft("");
          setOrgEmail("");
          setOrgEmailDraft("");
          setPhoneNumber("");
          setPhoneDraft("");
          setOfficeLocation("");
          setOfficeDraft("");
          setUseOrgBranding(true);
          setMembershipError(
            err.response?.data?.message ||
              "Could not load your organization profile."
          );
        }
      } finally {
        if (!cancelled) setMembershipLoading(false);
      }
    };

    loadMembershipProfile();
    return () => {
      cancelled = true;
    };
  }, [accessToken, user?.user_id, activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== "teams") return;
    if (!accessToken || !activeOrgId) {
      setMemberTeams([]);
      setMemberTeamsError(null);
      setMemberTeamsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setMemberTeamsLoading(true);
      setMemberTeamsError(null);
      try {
        const teams = await makeAuthenticatedRequest((token) =>
          fetchMemberTeams(token, activeOrgId)
        );
        if (!cancelled) setMemberTeams(teams);
      } catch (err) {
        console.error("Failed to load member teams:", err);
        if (!cancelled) {
          setMemberTeams([]);
          setMemberTeamsError(
            err.response?.data?.message || "Could not load your teams."
          );
        }
      } finally {
        if (!cancelled) setMemberTeamsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, accessToken, activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleOrgBranding = async (next) => {
    if (!accessToken || !user?.user_id || !activeOrgId || brandingSaving) return;
    const previous = useOrgBranding;
    setUseOrgBranding(next);
    setBrandingSaving(true);
    try {
      await makeAuthenticatedRequest(async (token) => {
        await axios.patch(
          `${API_BASE_URL}/org/${activeOrgId}/members/${user.user_id}/branding`,
          { use_org_branding: next },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });
      notifyOrgBrandChanged();
      toastSuccess(
        next
          ? "Using this workspace’s brand colors"
          : "Using Circa defaults for this workspace"
      );
    } catch (err) {
      setUseOrgBranding(previous);
      toastError(err, "Could not update brand preference");
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!accessToken || !user?.user_id || !activeOrgId || titleSaving) return;
    const next = titleDraft.trim().slice(0, TITLE_MAX_LEN);
    setTitleSaving(true);
    setTitleError(null);
    setTitleSuccess(false);
    try {
      const updated = await makeAuthenticatedRequest((token) =>
        updateMemberTitle(token, activeOrgId, user.user_id, next || null)
      );
      const saved = updated?.title || "";
      setMemberTitle(saved);
      setTitleDraft(saved);
      setTitleSuccess(true);
    } catch (err) {
      console.error("Failed to update title:", err);
      setTitleError(
        err.response?.data?.message || "Could not update your title."
      );
    } finally {
      setTitleSaving(false);
    }
  };

  const handleSaveOrgEmail = async () => {
    if (!accessToken || !user?.user_id || !activeOrgId || orgEmailSaving) return;
    const next = orgEmailDraft.trim();
    if (!isValidOrgEmail(next)) {
      setOrgEmailError("Please enter a valid email address");
      return;
    }
    setOrgEmailSaving(true);
    setOrgEmailError(null);
    setOrgEmailSuccess(false);
    try {
      const updated = await makeAuthenticatedRequest(async (token) => {
        const response = await axios.patch(
          `${API_BASE_URL}/org/${activeOrgId}/members/${user.user_id}/email`,
          { org_email: next },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
      });
      const saved = updated?.org_email || next.toLowerCase();
      setOrgEmail(saved);
      setOrgEmailDraft(saved);
      setOrgEmailSuccess(true);
    } catch (err) {
      console.error("Failed to update organization email:", err);
      setOrgEmailError(
        err.response?.data?.message || "Could not update your organization email."
      );
    } finally {
      setOrgEmailSaving(false);
    }
  };

  const handleSaveContact = async () => {
    if (!accessToken || !user?.user_id || !activeOrgId || contactSaving) return;
    const nextPhone = stripEmptyExtMarker(phoneDraft).slice(0, 40);
    const nextOffice = officeDraft.trim().slice(0, 120);
    if (nextPhone && !isValidOrgPhone(nextPhone)) {
      setContactError(
        "Use xxx-xxx-xxxx with an optional extension, for example 123-456-7890 ext 123."
      );
      return;
    }
    setContactSaving(true);
    setContactError(null);
    setContactSuccess(false);
    try {
      const updated = await makeAuthenticatedRequest(async (token) => {
        const response = await axios.patch(
          `${API_BASE_URL}/org/${activeOrgId}/members/${user.user_id}/profile`,
          {
            phone_number: nextPhone || null,
            office_location: nextOffice || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
      });
      const savedPhone = updated?.phone_number || "";
      const savedOffice = updated?.office_location || "";
      setPhoneNumber(savedPhone);
      setPhoneDraft(savedPhone);
      setOfficeLocation(savedOffice);
      setOfficeDraft(savedOffice);
      setContactSuccess(true);
    } catch (err) {
      console.error("Failed to update organization contact:", err);
      setContactError(
        err.response?.data?.message ||
          "Could not update your organization contact details."
      );
    } finally {
      setContactSaving(false);
    }
  };

  const handleClearTitle = async () => {
    if (!accessToken || !user?.user_id || !activeOrgId || titleSaving) return;
    setTitleDraft("");
    setTitleSaving(true);
    setTitleError(null);
    setTitleSuccess(false);
    try {
      await makeAuthenticatedRequest((token) =>
        updateMemberTitle(token, activeOrgId, user.user_id, null)
      );
      setMemberTitle("");
      setTitleDraft("");
      setTitleSuccess(true);
    } catch (err) {
      console.error("Failed to clear title:", err);
      setTitleDraft(memberTitle);
      setTitleError(
        err.response?.data?.message || "Could not clear your title."
      );
    } finally {
      setTitleSaving(false);
    }
  };
  if (loading) {
    return (
      <div className="flex flex-col bg-background text-foreground min-h-screen">
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-muted-foreground">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col bg-background text-foreground min-h-screen">
        <div className="flex-1 p-6">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col bg-background text-foreground min-h-screen">
        <div className="flex-1 p-6">
          <div className="text-muted-foreground">User not found</div>
        </div>
      </div>
    );
  }

  const avatarUser = {
    ...user,
    member_avatar_url: memberAvatarUrl,
  };

  return (
    <div className="flex flex-col bg-background text-foreground min-h-screen">
      <header className="flex items-center gap-4 border-b border-border p-4">
        <h1 className="text-3xl font-bold">
          {activeOrg ? `Settings for ${activeOrg.name}` : "Profile Settings"}
        </h1>
      </header>
      <div className="flex-1 p-6 overflow-y-auto">

        <Tabs value={activeTab} onValueChange={handleTabChange} className="max-w-2xl">
          <TabsList>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="posts">Posts</TabsTrigger>
            <TabsTrigger value="saved">Saved Posts</TabsTrigger>
            <TabsTrigger value="teams">Teams</TabsTrigger>
            <TabsTrigger value="drafts">Draft Posts</TabsTrigger>
            <TabsTrigger value="settings">Workspace</TabsTrigger>
            {activeOrgId ? (
              <TabsTrigger value="bots">My bots</TabsTrigger>
            ) : null}
            <TabsTrigger value="account">Circa account</TabsTrigger>
          </TabsList>

          {/* Profile Tab — workplace identity editors */}
          <TabsContent value="profile" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center">
                  <OrgMemberAvatar
                    user={avatarUser}
                    className="w-20 h-20 mr-5"
                    fallbackClassName="text-2xl font-bold bg-primary text-primary-foreground"
                  />
                  <div>
                    <CardTitle className="text-2xl">{getFullName(user)}</CardTitle>
                    {activeOrg ? (
                      <>
                        {memberTitle ? (
                          <CardDescription className="mt-1">{memberTitle}</CardDescription>
                        ) : (
                          <CardDescription className="mt-1 italic">
                            No title set for {activeOrg.name}
                          </CardDescription>
                        )}
                        <CardDescription className="mt-1">
                          Workplace profile for {activeOrg.name}
                        </CardDescription>
                      </>
                    ) : (
                      <CardDescription className="mt-1">
                        Select a workspace to manage your org email, title, and picture
                      </CardDescription>
                    )}
                    {user.created_at ? (
                      <CardDescription className="mt-1">
                        Member since{" "}
                        {new Date(user.created_at).toLocaleDateString()}
                      </CardDescription>
                    ) : null}
                  </div>
                </div>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Organization profile picture</CardTitle>
                <CardDescription>
                  {activeOrg
                    ? `This picture is used only inside ${activeOrg.name}. Switching organizations in the sidebar edits a different picture.`
                    : "Choose a workspace to set your per-organization profile picture."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!activeOrgId ? (
                  <p className="text-sm text-muted-foreground">
                    No workspace selected. Use the Workspace switcher at the top of the
                    sidebar, then return here to update your picture.
                  </p>
                ) : membershipLoading ? (
                  <p className="text-sm text-muted-foreground">Loading picture…</p>
                ) : membershipError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{membershipError}</AlertDescription>
                  </Alert>
                ) : (
                  <AvatarSourceEditor
                    label={`Picture for ${activeOrg?.name || "this organization"}`}
                    currentUrl={memberAvatarUrl}
                    previewFallback={
                      <span className="text-sm font-medium">
                        {getInitials(user)}
                      </span>
                    }
                    onSaveUrl={async (url) => {
                      const updated = await setMemberAvatarUrl(
                        accessToken,
                        activeOrgId,
                        user.user_id,
                        url
                      );
                      const next = updated.member_avatar_url || null;
                      setMemberAvatarUrlState(next);
                      window.dispatchEvent(
                        new CustomEvent("member-avatar-changed", {
                          detail: { orgId: activeOrgId, url: next },
                        })
                      );
                    }}
                    onUploadFile={async (file) => {
                      const updated = await uploadMemberAvatar(
                        accessToken,
                        activeOrgId,
                        user.user_id,
                        file
                      );
                      const next = updated.member_avatar_url || null;
                      setMemberAvatarUrlState(next);
                      window.dispatchEvent(
                        new CustomEvent("member-avatar-changed", {
                          detail: { orgId: activeOrgId, url: next },
                        })
                      );
                    }}
                    onClear={async () => {
                      const updated = await clearMemberAvatar(
                        accessToken,
                        activeOrgId,
                        user.user_id
                      );
                      const next = updated.member_avatar_url || null;
                      setMemberAvatarUrlState(next);
                      window.dispatchEvent(
                        new CustomEvent("member-avatar-changed", {
                          detail: { orgId: activeOrgId, url: next },
                        })
                      );
                    }}
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Organization title</CardTitle>
                <CardDescription>
                  {activeOrg
                    ? `Your role or title inside ${activeOrg.name} (shown on posts, messages, and member lists). Switching organizations edits a different title.`
                    : "Choose a workspace to set your per-organization title."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!activeOrgId ? (
                  <p className="text-sm text-muted-foreground">
                    No workspace selected. Use the Workspace switcher at the top of the
                    sidebar, then return here to update your title.
                  </p>
                ) : membershipLoading ? (
                  <p className="text-sm text-muted-foreground">Loading title…</p>
                ) : membershipError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{membershipError}</AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-3">
                    {titleSuccess && (
                      <Alert className="border-green-700 bg-green-900/20 text-green-200">
                        <AlertDescription>Title updated.</AlertDescription>
                      </Alert>
                    )}
                    {titleError && (
                      <Alert variant="destructive">
                        <AlertDescription>{titleError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="org-member-title">
                        Title for {activeOrg?.name || "this organization"}
                      </Label>
                      <Input
                        id="org-member-title"
                        type="text"
                        maxLength={TITLE_MAX_LEN}
                        placeholder="e.g. Product Manager"
                        value={titleDraft}
                        disabled={titleSaving}
                        onChange={(e) => {
                          setTitleDraft(e.target.value.slice(0, TITLE_MAX_LEN));
                          setTitleSuccess(false);
                          setTitleError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSaveTitle();
                          }
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        {titleDraft.trim().length}/{TITLE_MAX_LEN}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={
                          titleSaving ||
                          titleDraft.trim() === (memberTitle || "").trim()
                        }
                        onClick={handleSaveTitle}
                      >
                        {titleSaving ? "Saving…" : "Save title"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={titleSaving || !memberTitle}
                        onClick={handleClearTitle}
                      >
                        Clear title
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Organization email</CardTitle>
                <CardDescription>
                  {activeOrg
                    ? `Your contact email inside ${activeOrg.name}. This is separate from your Circa login email.`
                    : "Choose a workspace to set your per-organization contact email."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!activeOrgId ? (
                  <p className="text-sm text-muted-foreground">
                    No workspace selected. Use the Workspace switcher at the top of the
                    sidebar, then return here to update your email.
                  </p>
                ) : membershipLoading ? (
                  <p className="text-sm text-muted-foreground">Loading email…</p>
                ) : membershipError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{membershipError}</AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-3">
                    {orgEmailSuccess && (
                      <Alert className="border-green-700 bg-green-900/20 text-green-200">
                        <AlertDescription>
                          Organization email updated.
                        </AlertDescription>
                      </Alert>
                    )}
                    {orgEmailError && (
                      <Alert variant="destructive">
                        <AlertDescription>{orgEmailError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="org-member-email">
                        Email for {activeOrg?.name || "this organization"}
                      </Label>
                      <Input
                        id="org-member-email"
                        type="email"
                        placeholder="you@company.com"
                        value={orgEmailDraft}
                        disabled={orgEmailSaving}
                        onChange={(e) => {
                          setOrgEmailDraft(e.target.value);
                          setOrgEmailSuccess(false);
                          setOrgEmailError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSaveOrgEmail();
                          }
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        {orgEmail
                          ? "Unique within this organization. Does not change how you sign in."
                          : "Not set yet — your email stays blank in this organization until you set one. Does not change how you sign in."}
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={handleSaveOrgEmail}
                      disabled={
                        orgEmailSaving ||
                        orgEmailDraft.trim().toLowerCase() ===
                          (orgEmail || "").trim().toLowerCase()
                      }
                    >
                      {orgEmailSaving ? "Saving…" : "Save email"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Phone & office location</CardTitle>
                <CardDescription>
                  {activeOrg
                    ? `Optional contact details for ${activeOrg.name}. These stay with this workspace and are visible to other members.`
                    : "Choose a workspace to set phone and office location."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!activeOrgId ? (
                  <p className="text-sm text-muted-foreground">
                    No workspace selected. Use the Workspace switcher at the top of the
                    sidebar, then return here to update contact details.
                  </p>
                ) : membershipLoading ? (
                  <p className="text-sm text-muted-foreground">
                    Loading contact details…
                  </p>
                ) : membershipError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{membershipError}</AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-3">
                    {contactSuccess && (
                      <Alert className="border-green-700 bg-green-900/20 text-green-200">
                        <AlertDescription>
                          Contact details updated.
                        </AlertDescription>
                      </Alert>
                    )}
                    {contactError && (
                      <Alert variant="destructive">
                        <AlertDescription>{contactError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="org-member-phone">Phone number</Label>
                      <Input
                        id="org-member-phone"
                        type="tel"
                        maxLength={25}
                        inputMode="tel"
                        placeholder="123-456-7890 ext 123"
                        value={phoneDraft}
                        disabled={contactSaving}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Backspace" &&
                            hasEmptyExtMarker(phoneDraft)
                          ) {
                            e.preventDefault();
                            setPhoneDraft(stripEmptyExtMarker(phoneDraft));
                            setContactSuccess(false);
                            setContactError(null);
                          }
                        }}
                        onChange={(e) => {
                          setPhoneDraft(formatOrgPhoneInput(e.target.value));
                          setContactSuccess(false);
                          setContactError(null);
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        Format: xxx-xxx-xxxx, optionally followed by ext 123.
                        Leave blank to clear.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="org-member-office">Office location</Label>
                      <Input
                        id="org-member-office"
                        type="text"
                        maxLength={120}
                        placeholder="e.g. Floor 3 · Desk 12"
                        value={officeDraft}
                        disabled={contactSaving}
                        onChange={(e) => {
                          setOfficeDraft(e.target.value.slice(0, 120));
                          setContactSuccess(false);
                          setContactError(null);
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        {officeDraft.trim().length}/120 · Leave blank to clear
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={handleSaveContact}
                      disabled={
                        contactSaving ||
                        (phoneDraft.trim() === (phoneNumber || "").trim() &&
                          officeDraft.trim() === (officeLocation || "").trim())
                      }
                    >
                      {contactSaving ? "Saving…" : "Save contact details"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Post history</CardTitle>
                <CardDescription>
                  {activeOrg
                    ? `Your active feed posts in ${activeOrg.name}, newest first.`
                    : "Choose a workspace to view your posts there."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!activeOrgId ? (
                  <p className="text-sm text-muted-foreground">
                    No workspace selected. Use the Workspace switcher at the top of the
                    sidebar.
                  </p>
                ) : postHistoryError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{postHistoryError}</AlertDescription>
                  </Alert>
                ) : postHistoryLoading && postHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Loading posts…
                  </p>
                ) : postHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    You haven&apos;t published any feed posts in{" "}
                    {activeOrg?.name || "this organization"} yet.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {postHistoryTotal}{" "}
                      {postHistoryTotal === 1 ? "post" : "posts"}
                    </p>
                    <div className="space-y-3">
                      {postHistory.map((post) => {
                        const postId = post.post_id ?? post.postId;
                        const title =
                          markdownToPlain(post.title) ||
                          plainExcerpt(post.content, 80) ||
                          `Post #${postId}`;
                        const teamName =
                          post.team?.name ||
                          post.teamName ||
                          post.team_name ||
                          null;
                        const createdAt =
                          post.created_at || post.createdAt || null;
                        const href = permalinkForPost({
                          orgId: activeOrgId,
                          postId,
                        });
                        return (
                          <div
                            key={postId}
                            className="rounded-lg border border-border p-4 space-y-2"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <h3 className="font-medium text-foreground truncate">
                                  {title}
                                </h3>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {teamName ? `${teamName} · ` : ""}
                                  {createdAt
                                    ? new Date(createdAt).toLocaleString()
                                    : ""}
                                </p>
                              </div>
                              {href && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  asChild
                                  className="shrink-0"
                                >
                                  <Link to={href}>Open</Link>
                                </Button>
                              )}
                            </div>
                            {plainExcerpt(post.content, 220) && (
                              <p className="text-sm text-muted-foreground line-clamp-3">
                                {plainExcerpt(post.content, 220)}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {postHistory.length < postHistoryTotal && (
                      <div className="flex justify-center pt-1">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleLoadMorePosts}
                          disabled={postHistoryLoading}
                        >
                          {postHistoryLoading ? "Loading…" : "Load more"}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Saved Posts Tab */}
          <TabsContent value="saved" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Saved Posts</CardTitle>
                <CardDescription>
                  {activeOrg
                    ? `Posts you saved in ${activeOrg.name}, newest first.`
                    : "Choose a workspace to view posts you saved there."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!activeOrgId ? (
                  <p className="text-sm text-muted-foreground">
                    No workspace selected. Use the Workspace switcher at the top of the
                    sidebar.
                  </p>
                ) : savedPostsError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{savedPostsError}</AlertDescription>
                  </Alert>
                ) : savedPostsLoading && savedPosts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Loading saved posts…
                  </p>
                ) : savedPosts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    You haven&apos;t saved any posts in{" "}
                    {activeOrg?.name || "this organization"} yet.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {savedPostsTotal}{" "}
                      {savedPostsTotal === 1 ? "saved post" : "saved posts"}
                    </p>
                    <div className="space-y-3">
                      {savedPosts.map((post) => {
                        const postId = post.post_id ?? post.postId;
                        const title =
                          markdownToPlain(post.title) ||
                          plainExcerpt(post.content, 80) ||
                          `Post #${postId}`;
                        const teamName =
                          post.team?.name ||
                          post.teamName ||
                          post.team_name ||
                          null;
                        const savedAt = post.saved_at || post.savedAt || null;
                        const href = permalinkForPost({
                          orgId: activeOrgId,
                          postId,
                        });
                        return (
                          <div
                            key={postId}
                            className="rounded-lg border border-border p-4 space-y-2"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <h3 className="font-medium text-foreground truncate">
                                  {title}
                                </h3>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {teamName ? `${teamName} · ` : ""}
                                  {savedAt
                                    ? `Saved ${new Date(savedAt).toLocaleString()}`
                                    : ""}
                                </p>
                              </div>
                              {href && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  asChild
                                  className="shrink-0"
                                >
                                  <Link to={href}>Open</Link>
                                </Button>
                              )}
                            </div>
                            {plainExcerpt(post.content, 220) && (
                              <p className="text-sm text-muted-foreground line-clamp-3">
                                {plainExcerpt(post.content, 220)}
                              </p>
                            )}
                            <PostInteractions
                              postId={postId}
                              post={post}
                              orgId={activeOrgId}
                              likeCount={
                                post.like_count ?? post.likeCount ?? 0
                              }
                              commentCount={
                                post.comment_count ?? post.commentCount ?? 0
                              }
                              isLiked={!!(post.is_liked ?? post.isLiked)}
                              accessToken={accessToken}
                              onLike={() => handleSavedLike(post)}
                              onComment={() => {
                                if (href) navigate(href);
                              }}
                              onSavedChange={handleSavedChange}
                            />
                          </div>
                        );
                      })}
                    </div>
                    {savedPosts.length < savedPostsTotal && (
                      <div className="flex justify-center pt-1">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleLoadMoreSaved}
                          disabled={savedPostsLoading}
                        >
                          {savedPostsLoading ? "Loading…" : "Load more"}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Teams</CardTitle>
                <CardDescription>
                  {activeOrg
                    ? `Teams you belong to in ${activeOrg.name}.`
                    : "Choose a workspace to see your teams."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {!activeOrgId ? (
                  <p className="text-sm text-muted-foreground">
                    No workspace selected. Use the Workspace switcher at the top of the
                    sidebar.
                  </p>
                ) : memberTeamsError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{memberTeamsError}</AlertDescription>
                  </Alert>
                ) : memberTeamsLoading ? (
                  <p className="text-sm text-muted-foreground">Loading teams…</p>
                ) : memberTeams.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    You are not a member of any teams in{" "}
                    {activeOrg?.name || "this organization"}.
                  </p>
                ) : (
                  <ul className="divide-y divide-border rounded-lg border border-border">
                    {memberTeams.map((team) => {
                      const statusLabel =
                        team.status === "admin" ? "Admin" : "Member";
                      const href = `/teams/${team.team_id}?org_id=${encodeURIComponent(
                        activeOrgId
                      )}`;
                      return (
                        <li key={team.team_id}>
                          <Link
                            to={href}
                            className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                          >
                            <div className="min-w-0">
                              <p className="font-medium text-foreground truncate">
                                {team.name}
                              </p>
                              {team.description ? (
                                <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                                  {team.description}
                                </p>
                              ) : null}
                            </div>
                            <Badge variant="secondary" className="shrink-0">
                              {statusLabel}
                            </Badge>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Draft Posts Tab */}
          <TabsContent value="drafts" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Draft Posts</CardTitle>
                <CardDescription>
                  {activeOrg
                    ? `Saved drafts for ${activeOrg.name} — only you can see these until you publish.`
                    : "Choose a workspace to manage drafts."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!activeOrgId ? (
                  <p className="text-sm text-muted-foreground">
                    No workspace selected. Use the Workspace switcher at the top of the
                    sidebar.
                  </p>
                ) : draftsLoading && drafts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Loading drafts…</p>
                ) : draftsError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{draftsError}</AlertDescription>
                  </Alert>
                ) : editingDraft ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingDraftId(null)}
                        disabled={draftActionLoading}
                      >
                        ← Back to drafts
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleDraftDiscard}
                        disabled={draftActionLoading}
                      >
                        Discard
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Changes autosave while you edit. Publish when ready.
                    </p>
                    <PostComposer
                      mode="expanded"
                      destinationMode="picker"
                      destinations={draftComposerDestinations}
                      orgId={activeOrgId}
                      isSubmitting={draftActionLoading}
                      onSubmit={handleDraftPublish}
                      onSaveDraft={handleDraftAutosave}
                      autosave
                      submitLabel="Publish"
                      formKey={draftComposerKey}
                      initialTitle={editingDraft.title || ""}
                      initialContent={editingDraft.content || ""}
                      initialTags={editingDraft.tags || ""}
                      initialCritical={pendingCriticalFromMetadata(
                        editingDraft.metadata
                      )}
                      initialDestinationKey={
                        editingDraft.team_id != null ||
                        editingDraft.teamId != null
                          ? `team:${editingDraft.team_id ?? editingDraft.teamId}`
                          : `org:${activeOrgId}`
                      }
                      embedded
                      allowCritical={(dest) => {
                        if (!dest) return false;
                        if (canOrgPostCritical) return true;
                        if (dest.type === "team") {
                          return draftAdminTeamIds.has(Number(dest.id));
                        }
                        return false;
                      }}
                    />
                    {Array.isArray(editingDraft.attachments) &&
                    editingDraft.attachments.length > 0 ? (
                      <div className="space-y-1.5">
                        <p className="text-xs text-muted-foreground">
                          Attached files
                        </p>
                        <PostAttachmentChips
                          attachments={editingDraft.attachments}
                          postId={editingDraftId}
                          orgId={activeOrgId}
                          accessToken={accessToken}
                          stopPropagation={false}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : drafts.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center space-y-2">
                    <p className="text-sm font-medium text-foreground">
                      No drafts yet
                    </p>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      When you save a post as a draft, it will show up here so you
                      can finish and publish it later.
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-border rounded-md border border-border">
                    {drafts.map((draft) => {
                      const id = draft.post_id ?? draft.postId;
                      const destLabel = draft.team?.name
                        ? draft.team.name
                        : activeOrg?.name
                          ? `${activeOrg.name} (organization)`
                          : "Organization";
                      const updated =
                        draft.draft_updated_at ||
                        draft.draftUpdatedAt ||
                        draft.edited_at ||
                        draft.editedAt ||
                        draft.created_at ||
                        draft.createdAt;
                      return (
                        <li key={id}>
                          <button
                            type="button"
                            className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
                            onClick={() => {
                              setEditingDraftId(id);
                              setDraftComposerKey((k) => k + 1);
                            }}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 space-y-1">
                                <p className="text-sm font-medium text-foreground truncate">
                                  {draft.title?.trim() || "Untitled draft"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {destLabel}
                                  {updated
                                    ? ` · ${formatRelativeTime(updated)}`
                                    : ""}
                                </p>
                                {draft.content ? (
                                  <p className="text-xs text-muted-foreground line-clamp-2">
                                    {plainExcerpt(
                                      markdownToPlain(draft.content),
                                      140
                                    )}
                                  </p>
                                ) : null}
                              </div>
                              <Badge variant="secondary" className="shrink-0">
                                Draft
                              </Badge>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {draftsTotal > 0 && !editingDraft ? (
                  <p className="text-xs text-muted-foreground mt-3">
                    {draftsTotal} draft{draftsTotal === 1 ? "" : "s"}
                  </p>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Workspace Tab — notifications + branding */}
          <TabsContent value="settings" className="space-y-4">
            <NotificationPrefsPanel
              accessToken={accessToken}
              orgId={activeOrgId}
              orgName={activeOrg?.name}
            />

            <Card>
              <CardHeader>
                <CardTitle>Workspace brand colors</CardTitle>
                <CardDescription>
                  {activeOrg
                    ? `When on, Circa tints this workspace with ${activeOrg.name}'s brand—sidebar, page, cards, buttons, and the team board. Turn off to keep Circa defaults here only.`
                    : "Choose a workspace to control brand colors for that workspace."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!activeOrgId ? (
                  <p className="text-sm text-muted-foreground">
                    No workspace selected. Use the Workspace switcher at the top of the
                    sidebar, then return here.
                  </p>
                ) : membershipLoading ? (
                  <p className="text-sm text-muted-foreground">Loading preference…</p>
                ) : membershipError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{membershipError}</AlertDescription>
                  </Alert>
                ) : (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-input"
                      checked={useOrgBranding}
                      disabled={brandingSaving}
                      onChange={(e) => handleToggleOrgBranding(e.target.checked)}
                    />
                    <span className="space-y-1">
                      <span className="block text-sm font-medium">
                        Use {activeOrg?.name || "workspace"} brand colors
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        Only affects this organization (sidebar, page, cards,
                        buttons, and board). Other workspaces keep their own
                        setting.
                      </span>
                    </span>
                  </label>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bots" className="space-y-4">
            {activeOrgId && accessToken ? (
              <CircatronMyBotsSection
                orgId={activeOrgId}
                accessToken={accessToken}
                isIntegrationsAdmin={false}
              />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>My bots</CardTitle>
                  <CardDescription>
                    Select a workplace to manage personal Circatron bots for
                    that organization.
                  </CardDescription>
                </CardHeader>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="account" className="space-y-4">
            <CircaAccountPanel
              user={user}
              onUserUpdated={(updated) => setUser(updated)}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Profile;
